import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Activity, AlertTriangle, CheckCircle, Clock, Database, Mail, Server, XCircle } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import KPICard from "@/components/admin/KPICard";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface HealthCheck {
  status: "ok" | "error";
  latency?: number;
  error?: string;
}

interface HealthStatus {
  status: "healthy" | "degraded" | "down";
  timestamp: string;
  checks: {
    database: HealthCheck;
    storage: HealthCheck;
    email: HealthCheck;
  };
  totalLatency: number;
}

interface ErrorLog {
  id: string;
  error_type: string;
  error_message: string;
  severity: string;
  resolved: boolean;
  created_at: string;
  user_id?: string;
}

interface ActivityLog {
  id: string;
  action_type: string;
  user_email: string;
  resource_type: string;
  created_at: string;
}

export default function Monitoring() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [errorLogs, setErrorLogs] = useState<ErrorLog[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchHealthStatus = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("health-check");
      
      if (error) throw error;
      
      setHealthStatus(data);
    } catch (error) {
      console.error("Error fetching health status:", error);
      toast({
        title: "Errore",
        description: "Impossibile recuperare lo stato del sistema",
        variant: "error"
      });
    }
  };

  const fetchErrorLogs = async () => {
    const { data, error } = await supabase
      .from("error_logs" as any)
      .select("*")
      .eq("resolved", false)
      .order("created_at", { ascending: false })
      .limit(20);

    if (!error && data) {
      setErrorLogs(data as unknown as ErrorLog[]);
    }
  };

  const fetchActivityLogs = async () => {
    const { data, error } = await supabase
      .from("activity_logs" as any)
      .select("*")
      .order("created_at", { ascending: false })
      .limit(20);

    if (!error && data) {
      setActivityLogs(data as unknown as ActivityLog[]);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchHealthStatus(),
      fetchErrorLogs(),
      fetchActivityLogs()
    ]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAllData();

    // Auto-refresh every 30 seconds
    let interval: NodeJS.Timeout | null = null;
    if (autoRefresh) {
      interval = setInterval(fetchAllData, 30000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "healthy":
      case "ok":
        return "text-success";
      case "degraded":
        return "text-warning";
      case "down":
      case "error":
        return "text-destructive";
      default:
        return "text-muted-foreground";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "healthy":
      case "ok":
        return <CheckCircle className="w-5 h-5 text-success" />;
      case "degraded":
        return <AlertTriangle className="w-5 h-5 text-warning" />;
      case "down":
      case "error":
        return <XCircle className="w-5 h-5 text-destructive" />;
      default:
        return <Activity className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getSeverityBadge = (severity: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      low: "secondary",
      medium: "default",
      high: "destructive",
      critical: "destructive"
    };
    return <Badge variant={variants[severity] || "default"}>{severity}</Badge>;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">System Monitoring</h1>
            <p className="text-muted-foreground">Stato in tempo reale dei servizi</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              {autoRefresh ? "Disabilita" : "Abilita"} Auto-refresh
            </Button>
            <Button onClick={fetchAllData}>
              Aggiorna
            </Button>
          </div>
        </div>

        {/* Overall Status */}
        {healthStatus && (
          <BrutalCard className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {getStatusIcon(healthStatus.status)}
                <div>
                  <h2 className="text-2xl font-bold">
                    Sistema {healthStatus.status === "healthy" ? "Operativo" : healthStatus.status === "degraded" ? "Degradato" : "Non Disponibile"}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Ultimo aggiornamento: {new Date(healthStatus.timestamp).toLocaleString("it-IT")}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Latenza totale</div>
                <div className="text-2xl font-bold">{healthStatus.totalLatency}ms</div>
              </div>
            </div>
          </BrutalCard>
        )}

        {/* Service Health Checks */}
        {healthStatus && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <BrutalCard className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  <h3 className="font-bold">Database</h3>
                </div>
                {getStatusIcon(healthStatus.checks.database.status)}
              </div>
              {healthStatus.checks.database.latency && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Latenza: </span>
                  <span className="font-mono">{healthStatus.checks.database.latency}ms</span>
                </div>
              )}
              {healthStatus.checks.database.error && (
                <div className="text-sm text-destructive mt-2">
                  {healthStatus.checks.database.error}
                </div>
              )}
            </BrutalCard>

            <BrutalCard className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Server className="w-5 h-5" />
                  <h3 className="font-bold">Storage</h3>
                </div>
                {getStatusIcon(healthStatus.checks.storage.status)}
              </div>
              {healthStatus.checks.storage.latency && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Latenza: </span>
                  <span className="font-mono">{healthStatus.checks.storage.latency}ms</span>
                </div>
              )}
              {healthStatus.checks.storage.error && (
                <div className="text-sm text-destructive mt-2">
                  {healthStatus.checks.storage.error}
                </div>
              )}
            </BrutalCard>

            <BrutalCard className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  <h3 className="font-bold">Email Service</h3>
                </div>
                {getStatusIcon(healthStatus.checks.email.status)}
              </div>
              {healthStatus.checks.email.latency && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Latenza: </span>
                  <span className="font-mono">{healthStatus.checks.email.latency}ms</span>
                </div>
              )}
              {healthStatus.checks.email.error && (
                <div className="text-sm text-destructive mt-2">
                  {healthStatus.checks.email.error}
                </div>
              )}
            </BrutalCard>
          </div>
        )}

        {/* Error Logs */}
        <BrutalCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Errori Recenti
            </h2>
            <Badge variant="destructive">{errorLogs.length} non risolti</Badge>
          </div>
          {errorLogs.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Nessun errore da mostrare</p>
          ) : (
            <div className="space-y-3">
              {errorLogs.slice(0, 10).map((log) => (
                <div key={log.id} className="border-l-4 border-destructive pl-4 py-2">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {getSeverityBadge(log.severity)}
                        <span className="text-sm font-mono text-muted-foreground">{log.error_type}</span>
                      </div>
                      <p className="text-sm truncate">{log.error_message}</p>
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString("it-IT")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </BrutalCard>

        {/* Recent Activity */}
        <BrutalCard className="p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Attività Recente
          </h2>
          {activityLogs.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Nessuna attività da mostrare</p>
          ) : (
            <div className="space-y-2">
              {activityLogs.slice(0, 10).map((log) => (
                <div key={log.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{log.action_type}</Badge>
                    <span className="text-sm">{log.user_email}</span>
                    {log.resource_type && (
                      <span className="text-xs text-muted-foreground">→ {log.resource_type}</span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(log.created_at).toLocaleString("it-IT")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </BrutalCard>
      </div>
    </AdminLayout>
  );
}
